import type { DataTableColumnTextAlignment } from '@repo/mantine-datatable';
import { useMemo } from 'react';
import type { Measurement, Monitor } from '../../../api';
import type { ExtendedDataTableColumn } from '../../../components/TableV2/types.ts';

import type { MetricType } from '@repo/api-codegen';
import { formatMonitorMetricValue } from '@repo/common/utils';
import dayjs from 'dayjs';
import { DateRenderer } from '../../../components/CatalogView/TableView/DateRenderer';
import { IncidentStatusBadge } from '../components/IncidentStatusBadge.tsx';
import { metricTypeLabel } from '../utils.tsx';

export function useColumns(
	monitor: Monitor
): ExtendedDataTableColumn<Measurement>[] {
	const columns = useMemo(
		() => [
			{
				accessor: 'created_at',
				titleLabel: 'Time',
				width: 200,
				render: (record: Measurement) =>
					record?.created_at && (
						<DateRenderer
							value={record.created_at}
							format="MMM DD, YYYY [at] hh:mm A"
							size="xs"
						/>
					),
			},
			{
				accessor: 'value',
				titleLabel: 'Last refresh',
				width: 200,
				hidden: monitor.metric_type !== 'freshness',
				render: (record: Measurement) =>
					record?.created_at && (
						<DateRenderer
							value={dayjs(record.created_at)
								.subtract(Number(record.value), 'seconds')
								.format()}
							format="MMM DD, YYYY [at] hh:mm A"
							size="xs"
						/>
					),
			},
			{
				accessor: 'value',
				titleLabel: metricTypeLabel(monitor.metric_type),
				width: 'auto',
				textAlignment: 'right' as DataTableColumnTextAlignment,
				render: (record: Measurement) =>
					formatMonitorMetricValue(
						monitor.metric_type as MetricType,
						parseFloat(record.value)
					),
			},
			{
				accessor: 'incident_status',
				titleLabel: 'Incident',
				width: 100,
				render: (record: Measurement) =>
					record.incident_status ? (
						<IncidentStatusBadge incidentStatus={record.incident_status} />
					) : null,
			},
		],
		[monitor.metric_type]
	);

	return columns;
}
